import { useMutation } from "@apollo/client";
import { useCallback, useState } from "react";
import {
  SetRelationshipMutationFromAccountMutation,
  SetRelationshipMutationFromAccountMutationVariables,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import setRelationshipMutationFromAccount from "@apps/account/graphql/set-relationship-mutation";
import Loading from "@components/loading/loading";
import useUserComboboxQuery from "@components/user-combobox/use-user-combobox-query";
import UserCombobox from "@components/user-combobox/user-combobox";
import {
  UserComboboxOption,
  UserComboboxOptionType,
  UserComboboxUserOption,
} from "@components/user-combobox/user-combobox-list";
import { classNames } from "@helpers/css";

const MemberManagerCombobox = ({
  excludeUserIds,
  user,
  manager,
  organization,
  primaryManagerId,
}: {
  excludeUserIds: number[];
  user: BasicUser;
  manager: BasicUser | null;
  organization: {
    id: number;
  };
  primaryManagerId?: number;
}) => {
  // Hooks
  const [canLoadData, setCanLoadData] = useState(false);

  const [setRelationship, { loading: relationshipLoading }] = useMutation<
    SetRelationshipMutationFromAccountMutation,
    SetRelationshipMutationFromAccountMutationVariables
  >(setRelationshipMutationFromAccount);

  const selectedUser: UserComboboxUserOption | undefined = manager
    ? { type: UserComboboxOptionType.USER, ...manager }
    : undefined;
  const { loading, options, query, setQuery } = useUserComboboxQuery({
    queryOptions: {
      skip: !canLoadData,
    },
    selected: selectedUser,
    excludeUserIds: excludeUserIds,
  });

  // Handlers
  const removeManager = useCallback(() => {
    if (manager) {
      return setRelationship({
        variables: {
          userId: user.id,
          organizationId: organization.id,
          clearRelationship: true,
          otherUserId: manager.id,
        },
      });
    }
    return Promise.resolve();
  }, [manager, organization, setRelationship, user.id]);

  const handleChangeManager = useCallback(
    (newManager: UserComboboxOption | null) => {
      setQuery("");
      removeManager().then(() => {
        if (newManager?.id) {
          setRelationship({
            variables: {
              userId: user.id,
              organizationId: organization.id,
              clearRelationship: null,
              otherUserId: newManager.id as number,
            },
          });
        }
      });
    },
    [organization.id, removeManager, setQuery, setRelationship, user.id],
  );

  const handlePreloadData = useCallback(() => {
    setCanLoadData(true);
  }, []);

  const handleClearValue = useCallback(() => {
    handleChangeManager(null);
  }, [handleChangeManager]);

  const removePrimaryManager = useCallback(() => {
    if (primaryManagerId) {
      return setRelationship({
        variables: {
          userId: user.id,
          organizationId: organization.id,
          otherUserId: primaryManagerId,
          isPrimaryRelationship: false,
        },
      });
    }
    return Promise.resolve();
  }, [primaryManagerId, organization, setRelationship, user.id]);

  const handleChangePrimaryManager = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newPrimaryManagerId = Number(e.target.value);
    setQuery("");
    if (newPrimaryManagerId) {
      removePrimaryManager().then(() => {
        setRelationship({
          variables: {
            userId: user.id,
            organizationId: organization.id,
            clearRelationship: null,
            otherUserId: newPrimaryManagerId,
            isPrimaryRelationship: true,
          },
        });
      });
    }
  };

  const isPrimaryManager = primaryManagerId === manager?.id;

  return (
    <div className="flex-1 flex gap-4 items-center">
      <div className="flex-1" onMouseEnter={handlePreloadData}>
        <UserCombobox
          aria-label="Manager combobox"
          placeholder="Add manager..."
          disabled={relationshipLoading}
          loading={loading}
          query={query}
          value={selectedUser ?? null}
          options={options}
          clearable={!!selectedUser?.id}
          onChangeValue={handleChangeManager}
          onChangeQuery={setQuery}
          onClickButton={handlePreloadData}
          onClearValue={handleClearValue}
        />
      </div>
      {manager && (
        <label
          className={classNames(
            "cursor-pointer flex items-center gap-1 text-sm text-gray-600",
            isPrimaryManager ? "opacity-100" : "opacity-80 hover:opacity-100",
          )}
        >
          <input
            type="radio"
            name="manager"
            value={manager.id}
            checked={isPrimaryManager}
            onChange={handleChangePrimaryManager}
          />
          Primary
        </label>
      )}
      {manager && (
        <Loading
          mini
          size="5"
          className={relationshipLoading ? "opacity-1" : "opacity-0"}
        />
      )}
    </div>
  );
};

export default MemberManagerCombobox;
